"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { Loader2, Zap, ZapOff } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import {
  fetchAutoTradeSettings,
  fetchCycleProgress,
  type AutoTradeSettings,
  type CycleProgressState,
} from "@/lib/agent-force"

const SETTINGS_POLL_MS = 60_000
const PROGRESS_POLL_MS = 5_000
const TICK_MS = 1_000

function parseTimeMs(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime()
  return Number.isFinite(ms) ? ms : null
}

function computeNextRunMs(lastRunAt: string | null | undefined, intervalMinutes: number): number | null {
  const last = parseTimeMs(lastRunAt)
  if (last == null) return null
  const intervalMs = Math.max(1, intervalMinutes) * 60 * 1000
  let next = last + intervalMs
  const now = Date.now()
  while (next <= now) next += intervalMs
  return next
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "now"
  const totalSec = Math.ceil(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60)
  const rm = m % 60
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`
}

function lastResultLabel(
  progress: CycleProgressState | null,
  settings: AutoTradeSettings | null,
): { text: string; tone: "positive" | "warning" | "destructive" | "muted" | "primary" } {
  if (progress && !progress.done && progress.phase !== "idle") {
    return { text: "Running…", tone: "primary" }
  }
  if (progress?.done && progress.result_status === "trade_selected") {
    const sym = progress.selected_symbol ? ` · ${progress.selected_symbol}` : ""
    return { text: `Trade${sym}`, tone: "positive" }
  }
  if (progress?.done && progress.result_status === "no_trade") {
    return { text: "No trade", tone: "muted" }
  }
  if ((progress?.done && progress.result_status === "error") || progress?.phase === "error") {
    return { text: "Error", tone: "destructive" }
  }
  if (settings?.last_error) {
    return { text: "Error", tone: "destructive" }
  }
  if (settings?.last_run_at) {
    return { text: "Completed", tone: "muted" }
  }
  return { text: "—", tone: "muted" }
}

export function AutotradeHeaderSummary({ layout = "panel" }: { layout?: "panel" | "inline" }) {
  const [settings, setSettings] = useState<AutoTradeSettings | null>(null)
  const [progress, setProgress] = useState<CycleProgressState | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const loadSettings = useCallback(async () => {
    try {
      setSettings(await fetchAutoTradeSettings())
    } catch {
      // keep last good snapshot
    }
  }, [])

  const loadProgress = useCallback(async () => {
    try {
      setProgress(await fetchCycleProgress())
    } catch {
      // endpoint may be absent on older backends
    }
  }, [])

  useEffect(() => {
    void loadSettings()
    const id = window.setInterval(() => void loadSettings(), SETTINGS_POLL_MS)
    return () => window.clearInterval(id)
  }, [loadSettings])

  useEffect(() => {
    void loadProgress()
    const id = window.setInterval(() => void loadProgress(), PROGRESS_POLL_MS)
    return () => window.clearInterval(id)
  }, [loadProgress])

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(id)
  }, [])

  const enabled = settings?.enabled ?? false
  const intervalMin = settings?.interval_minutes ?? 15
  const nextRunMs = useMemo(
    () => (enabled ? computeNextRunMs(settings?.last_run_at, intervalMin) : null),
    [enabled, settings?.last_run_at, intervalMin],
  )
  const countdown =
    nextRunMs != null ? formatCountdown(Math.max(0, nextRunMs - now)) : enabled ? "—" : null
  const result = lastResultLabel(progress, settings)
  const cycleLive = Boolean(progress && !progress.done && progress.phase !== "idle")

  if (!settings && !progress) return null

  const resultToneClass =
    result.tone === "positive"
      ? "text-positive"
      : result.tone === "destructive"
        ? "text-destructive"
        : result.tone === "warning"
          ? "text-warning"
          : result.tone === "primary"
            ? "text-primary"
            : "text-muted-foreground"

  const statusBadge = enabled ? (
    <Badge
      variant="outline"
      className={cn(
        "h-5 gap-1 px-1.5 text-[10px] font-medium shrink-0",
        cycleLive
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-positive/40 bg-positive/10 text-positive",
      )}
    >
      {cycleLive ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
      {cycleLive ? "Cycle live" : "Auto ON"}
    </Badge>
  ) : (
    <Badge
      variant="outline"
      className="h-5 gap-1 px-1.5 text-[10px] font-medium text-muted-foreground shrink-0"
    >
      <ZapOff className="h-3 w-3" />
      Auto OFF
    </Badge>
  )

  const tooltip =
    settings?.last_error
      ? String(settings.last_error)
      : progress?.error
        ? String(progress.error)
        : undefined

  if (layout === "inline") {
    return (
      <div
        className="flex shrink-0 items-center gap-2 whitespace-nowrap text-[10px] leading-none"
        title={tooltip}
      >
        {statusBadge}
        {enabled && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              Next <span className="font-mono font-medium text-foreground tabular-nums">{countdown ?? "—"}</span>
            </span>
          </>
        )}
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">
          Last <span className={cn("font-medium", resultToneClass)}>{result.text}</span>
        </span>
        {enabled && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{intervalMin}m cycle</span>
          </>
        )}
      </div>
    )
  }

  return (
    <div
      className="flex max-w-[min(100%,20rem)] flex-col gap-0.5 rounded-md border border-border bg-secondary/30 px-2 py-1.5 sm:max-w-none sm:px-2.5"
      title={tooltip}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {statusBadge}
        {enabled && (
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
            every {intervalMin}m
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0 text-[10px] leading-tight">
        {enabled && (
          <span className="text-muted-foreground whitespace-nowrap">
            Next{" "}
            <span className="font-mono font-medium text-foreground tabular-nums">{countdown ?? "—"}</span>
          </span>
        )}
        <span className="text-muted-foreground whitespace-nowrap">
          Last{" "}
          <span className={cn("font-medium", resultToneClass)}>{result.text}</span>
        </span>
      </div>
    </div>
  )
}
